import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTenant } from '../context/TenantContext'
import { useTimezone } from '../context/TimezoneContext'
import { FALLBACK_ACTIVITY_TYPES } from '../../modules/time/categoryUtils'
import { formatDuration } from '../utils/dateUtils'
import Modal from './ui/Modal'
import NeedsAttention from './NeedsAttention'
import {
  ClockIcon,
  ClipboardDocumentListIcon,
  TruckIcon,
  CheckCircleIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

function Dashboard() {
  const { user, getAccessToken } = useAuth()
  const { tenant, modules } = useTenant()
  const { formatDate } = useTimezone()
  const [reportsStats, setReportsStats] = useState(null)
  const [timeStats, setTimeStats] = useState(null)
  const [todoData, setTodoData] = useState({ todoIncidents: [], recentEvents: [] })
  const [statsLoading, setStatsLoading] = useState(true)
  const [quickLog, setQuickLog] = useState(null) // { type: 'incident'|'activity', data: {...} }
  const [quickLogSaving, setQuickLogSaving] = useState(false)
  const [quickLogError, setQuickLogError] = useState(null)
  const [quickLogNotes, setQuickLogNotes] = useState('')
  const [quickLogHours, setQuickLogHours] = useState('')


  // Load real stats
  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setStatsLoading(true)
      const token = getAccessToken()
      const headers = token ? { Authorization: `Bearer ${token}` } : {}

      // Fetch reports stats
      const reportsRes = await fetch('/api/reports/stats/summary', { headers })
      const reportsData = await reportsRes.json()
      if (reportsData.success) {
        setReportsStats(reportsData.stats)
      }

      // Fetch time stats
      const timeRes = await fetch('/api/time/stats/summary', { headers })
      const timeData = await timeRes.json()
      if (timeData.success) {
        setTimeStats(timeData.stats)
      }

      // Fetch todo data
      const todoRes = await fetch('/api/time/todo', { headers })
      const todoResult = await todoRes.json()
      if (todoResult.success) {
        setTodoData({
          todoIncidents: todoResult.todoIncidents || [],
          recentEvents: todoResult.recentEvents || []
        })
      }

    } catch (err) {
      console.error('Failed to load stats:', err)
    } finally {
      setStatsLoading(false)
    }
  }

  const openQuickLog = (type, data) => {
    setQuickLog({ type, data })
    setQuickLogError(null)
    setQuickLogNotes('')
    setQuickLogHours(data.defaultDurationMinutes ? (data.defaultDurationMinutes / 60).toString() : '')
  }

  const submitQuickLog = async () => {
    setQuickLogSaving(true)
    setQuickLogError(null)
    try {
      const token = getAccessToken()
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }

      let payload
      if (quickLog.type === 'incident') {
        const todo = quickLog.data
        payload = {
          eventType: 'incident',
          eventId: todo.incidentId,
          eventName: `Incident #${todo.incidentNumber}`,
          useCallDuration: true,
          apparatus: todo.apparatus || '',
          date: todo.incidentDate,
          durationMinutes: 0,
          notes: quickLogNotes
        }
      } else {
        const activity = quickLog.data
        const isAttendanceOnly = !!activity.attendanceOnly
        payload = {
          eventType: activity.type || 'training',
          eventId: activity.id,
          eventInstanceId: activity.instanceId,
          eventName: activity.name,
          attendanceOnly: isAttendanceOnly,
          date: activity.instanceDate ? String(activity.instanceDate).split('T')[0] : null,
          durationMinutes: isAttendanceOnly ? 0 : Math.round((parseFloat(quickLogHours) || 0) * 60),
          notes: quickLogNotes
        }
      }

      const res = await fetch('/api/time', { method: 'POST', headers, body: JSON.stringify(payload) })
      const data = await res.json()
      if (data.success) {
        setQuickLog(null)
        loadStats() // refresh todo lists
      } else {
        setQuickLogError(data.error || 'Failed to save')
      }
    } catch (err) {
      setQuickLogError('Failed to save')
    } finally {
      setQuickLogSaving(false)
    }
  }

  // Get greeting based on time of day
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good morning'
    if (hour < 18) return 'Good afternoon'
    return 'Good evening'
  }

  return (
    <div className="space-y-6">
      {/* Welcome section */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-amber-600 flex items-center justify-center text-white text-xl font-bold">
            {user?.name?.charAt(0) || '?'}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">
              {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}!
            </h1>
            <p className="text-slate-400">
              Welcome to {tenant?.name || 'Scenelight'}
            </p>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          label="Draft Reports"
          value={statsLoading ? '...' : (reportsStats?.draft || 0)}
          Icon={ClipboardDocumentListIcon}
          link="/reports"
        />
        <StatCard
          label="Ready to Upload"
          value={statsLoading ? '...' : (reportsStats?.ready || 0)}
          Icon={CheckCircleIcon}
          link="/reports"
        />
        <StatCard
          label="Incidents (Month)"
          value={statsLoading ? '...' : (reportsStats?.thisMonth || 0)}
          Icon={TruckIcon}
          link="/reports"
        />
        <StatCard
          label="Hours (Month)"
          value={statsLoading ? '...' : formatDuration(timeStats?.thisMonth?.minutes || 0)}
          Icon={ClockIcon}
          link="/time"
        />
      </div>

      {/* Open items from every module */}
      <NeedsAttention />

      {/* To-Do sections - only if time_tracker is enabled */}
      {modules.some(m => m.is_enabled && m.module_name === 'time_tracker') && (todoData.todoIncidents.length > 0 || todoData.recentEvents.length > 0) && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-4">To Do</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {/* To-Do: Incidents */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold">To-Do: Incidents</h3>
                <p className="text-slate-500 text-xs mt-1">Incidents you responded to that need time logged</p>
              </div>
              {todoData.todoIncidents.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-slate-400">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {todoData.todoIncidents.map(todo => {
                    const deadlineColor = todo.daysUntilDeadline > 3
                      ? 'bg-green-500/20 text-green-400'
                      : todo.daysUntilDeadline >= 1
                        ? 'bg-amber-500/20 text-amber-400'
                        : 'bg-red-500/20 text-red-400'
                    return (
                      <div key={todo.incidentId} className="p-4 hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                            <TruckIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <a href={`/reports?id=${todo.incidentId}`} className="font-medium text-white hover:text-blue-400 transition-colors">
                                Incident #{todo.incidentNumber}
                              </a>
                              <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${deadlineColor}`}>
                                {todo.daysUntilDeadline > 0 ? `${todo.daysUntilDeadline}d left` : 'Overdue'}
                              </span>
                            </div>
                            <div className="text-slate-400 text-sm mt-0.5">{todo.address || 'No address'}</div>
                            <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                              <span>{formatDate(todo.incidentDate)}</span>
                              {todo.callDurationMinutes && <span>Call: {formatDuration(todo.callDurationMinutes)}</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => openQuickLog('incident', todo)}
                            className="px-3 py-1.5 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg text-sm font-medium whitespace-nowrap"
                          >
                            Log Time
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* To-Do: Events */}
            <div className="bg-slate-800 border border-slate-700 rounded-xl">
              <div className="p-4 border-b border-slate-700">
                <h3 className="text-white font-semibold">To-Do: Events</h3>
                <p className="text-slate-500 text-xs mt-1">Events from the last 30 days you haven't logged</p>
              </div>
              {todoData.recentEvents.length === 0 ? (
                <div className="p-8 text-center">
                  <CheckIcon className="w-8 h-8 text-green-400 mx-auto mb-2" />
                  <p className="text-slate-400">You're all caught up!</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {todoData.recentEvents.map(event => {
                    const typeConfig = FALLBACK_ACTIVITY_TYPES[event.type] || FALLBACK_ACTIVITY_TYPES.other
                    const TypeIcon = typeConfig.Icon
                    return (
                      <div key={`${event.id}-${event.instanceId}`} className="p-4 hover:bg-slate-700/30 transition-colors">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${typeConfig.bgColor} ${typeConfig.color}`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium text-white">{event.name}</span>
                            {event.description && <p className="text-xs text-slate-400 mt-0.5">{event.description}</p>}
                            <div className="text-xs text-slate-500 mt-1">
                              {formatDate(event.instanceDate + 'T12:00:00')}
                            </div>
                          </div>
                          <button
                            onClick={() => openQuickLog('activity', event)}
                            className="px-3 py-1.5 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 rounded-lg text-sm font-medium whitespace-nowrap"
                          >
                            Log Time
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Log Modal */}
      <Modal
        isOpen={!!quickLog}
        onClose={() => setQuickLog(null)}
        title="Log Time"
        size="sm"
      >
        {quickLog && (
          <div className="p-4 space-y-4">
            {quickLogError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-400">{quickLogError}</p>
              </div>
            )}

            {quickLog.type === 'incident' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                    <TruckIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Incident #{quickLog.data.incidentNumber}</p>
                    <p className="text-sm text-slate-400">{quickLog.data.address || 'No address'}</p>
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Date</span>
                    <span className="text-white">{formatDate(quickLog.data.incidentDate)}</span>
                  </div>
                  {quickLog.data.callDurationMinutes && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Call Duration</span>
                      <span className="text-white">{formatDuration(quickLog.data.callDurationMinutes)}</span>
                    </div>
                  )}
                  {quickLog.data.apparatus && (
                    <div className="flex justify-between">
                      <span className="text-slate-400">Apparatus</span>
                      <span className="text-white">{quickLog.data.apparatus}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-500">Time will be calculated from full call duration (alarm to cleared).</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  {(() => {
                    const typeConfig = FALLBACK_ACTIVITY_TYPES[quickLog.data.type] || FALLBACK_ACTIVITY_TYPES.other
                    const TypeIcon = typeConfig.Icon
                    return (
                      <div className={`p-2 rounded-lg ${typeConfig.bgColor} ${typeConfig.color}`}>
                        <TypeIcon className="w-5 h-5" />
                      </div>
                    )
                  })()}
                  <div>
                    <p className="font-medium text-white">{quickLog.data.name}</p>
                    {quickLog.data.description && <p className="text-sm text-slate-400">{quickLog.data.description}</p>}
                  </div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Date</span>
                    <span className="text-white">{formatDate(quickLog.data.instanceDate + 'T12:00:00')}</span>
                  </div>
                </div>
                {quickLog.data.attendanceOnly ? (
                  <p className="text-xs text-slate-500">This event only tracks attendance, not hours.</p>
                ) : (
                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Hours</label>
                    <select
                      value={quickLogHours}
                      onChange={(e) => setQuickLogHours(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                    >
                      <option value="">Select hours...</option>
                      {Array.from({ length: 96 }, (_, i) => {
                        const h = (i + 1) * 0.25
                        const hrs = Math.floor(h)
                        const mins = Math.round((h % 1) * 60)
                        const label = mins === 0 ? `${hrs}h` : hrs === 0 ? `${mins}m` : `${hrs}h ${mins}m`
                        return <option key={h} value={h}>{label}</option>
                      })}
                    </select>
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm text-slate-400 mb-2">Notes (optional)</label>
              <textarea
                value={quickLogNotes}
                onChange={(e) => setQuickLogNotes(e.target.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white resize-none text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
              <button
                onClick={() => setQuickLog(null)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={submitQuickLog}
                disabled={quickLogSaving || (quickLog?.type !== 'incident' && !quickLog?.data?.attendanceOnly && !quickLogHours)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-semibold text-sm disabled:opacity-50"
              >
                {quickLogSaving ? 'Saving...' : 'Log Time'}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

function StatCard({ label, value, Icon, trend, trendColor, link }) {
  const content = (
    <div className="card h-full hover:border-slate-600 transition-colors">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {trend && (
            <p className={`text-xs mt-1 ${trendColor || 'text-slate-500'}`}>{trend}</p>
          )}
        </div>
        <Icon className="w-6 h-6 text-slate-500" />
      </div>
    </div>
  )

  if (link) {
    return <Link to={link} className="block">{content}</Link>
  }
  return content
}

export default Dashboard
